import {qs} from '@shared/helpers/queryString'

import {DeliveryTagTypeValues, OrderSheetPageSet, OrderSheetPageSetType} from '@web/core/constants'
import getApi, {createWrapper, getBaseUrl, getCommonBaseUrl} from '@web/helpers/api'

import {
    AddEntranceResponse,
    ArrivalGuaranteeValidateResponse,
    ChangeDeliveryParams,
    ChangeDeliveryResponse,
    DeleteEntranceResponse,
    DeliveriesAddressFeeResponse,
    EditEntranceResponse,
    Entrance,
    GetEntranceResponse,
    ValidateAddressResponse,
} from './interfaces'

const DeliveryApiUrlInfo = {
    [OrderSheetPageSet.MALL]: {
        /** 변경 후 재계산 요청 */
        deliveryFee: '/orderSheet/mall/deliveries/fee',
        /** 폐지된 우편번호 리스트 조회 */
        deprecatedZipCodes: '/orderSheet/mall/address/deprecatedZipCodes',
    },
    [OrderSheetPageSet.EASYBOOKING]: {
        /** 변경 후 재계산 요청 */
        deliveryFee: '/orderSheet/easyBooking/deliveries/fee',
        /** 폐지된 우편번호 리스트 조회 */
        deprecatedZipCodes: '/orderSheet/easyBooking/address/deprecatedZipCodes',
    },
    [OrderSheetPageSet.SELLER]: {
        /** 변경 후 재계산 요청 */
        deliveryFee: '/orderSheet/seller/deliveries/fee',
        /** 가주문 생성 전 도착보장 상품의 배송정보 변경 여부 체크 */
        arrivalGuaranteeValidate: '/orderSheet/seller/arrivalGuaranteeValidate',
        /** 폐지된 우편번호 리스트 조회 */
        deprecatedZipCodes: '/orderSheet/seller/address/deprecatedZipCodes',
    },
    // 패밀리 결제는 배송지 조회가 없습니다.
    [OrderSheetPageSet.FAMILY_PAY]: {
        deliveryFee: '',
        arrivalGuaranteeValidate: '',
        deprecatedZipCodes: '',
    },
    // 클레임 비용결제는 배송지 조회가 없습니다.
    [OrderSheetPageSet.CLAIM]: {
        deliveryFee: '',
        arrivalGuaranteeValidate: '',
        deprecatedZipCodes: '',
    },
}

/** 변경 후 재계산 요청 */
async function getDeliveryAddressFee(
    pageSet: OrderSheetPageSetType,
    {
        orderSheetId,
        zipCode,
        buildingManagementNo,
        locationX,
        locationY,
        address,
        arrivalGuaranteesByOrderSheetProductId,
    }: {
        orderSheetId: string
        zipCode: string
        buildingManagementNo: Maybe<string>
        locationX: Maybe<string>
        locationY: Maybe<string>
        address: string
        arrivalGuaranteesByOrderSheetProductId: Maybe<
            Record<
                string,
                {
                    arrivalGuaranteeDateTime: string
                    rmid: Maybe<string>
                    outboundLocationId: Maybe<string>
                    deliveryTagType?: DeliveryTagTypeValues
                }
            >
        >
    },
) {
    const url = DeliveryApiUrlInfo[pageSet].deliveryFee
    const baseUrl = getBaseUrl(pageSet)
    const api = getApi(baseUrl)
    return createWrapper<DeliveriesAddressFeeResponse>(
        api.post(url, {
            orderSheetId,
            deliveryRequest: {
                zipCode,
                address,
                ...(pageSet === OrderSheetPageSet.SELLER && {arrivalGuaranteesByOrderSheetProductId}),
                ...(buildingManagementNo && {buildingManagementNo}),
                ...(locationX && {locationX}),
                ...(locationY && {locationY}),
            },
        }),
    )
}

/** 가주문 생성 전 도착보장 상품의 배송정보 변경 여부 체크 */
async function getArrivalGuaranteeValidate({
    orderSheetId,
    zipCode,
    buildingManagementNo,
    locationX,
    locationY,
    arrivalGuaranteesByOrderSheetProductId,
    isSubscription,
}: {
    orderSheetId: string
    zipCode: string
    buildingManagementNo: Maybe<string>
    locationX: Maybe<string>
    locationY: Maybe<string>
    arrivalGuaranteesByOrderSheetProductId: Record<
        string,
        {
            arrivalGuaranteeDateTime: string
            rmid: Maybe<string>
            outboundLocationId: Maybe<string>
            deliveryTagType?: DeliveryTagTypeValues
        }
    >
    isSubscription?: boolean
}) {
    const url = DeliveryApiUrlInfo[OrderSheetPageSet.SELLER].arrivalGuaranteeValidate
    const baseUrl = getBaseUrl(OrderSheetPageSet.SELLER)
    const api = getApi(baseUrl)
    return createWrapper<ArrivalGuaranteeValidateResponse>(
        api.post(url, {
            orderSheetId,
            arrivalGuaranteeRequest: {
                zipCode,
                ...(buildingManagementNo && {buildingManagementNo}),
                ...(locationX && {locationX}),
                ...(locationY && {locationY}),
                arrivalGuaranteesByOrderSheetProductId,
                isSubscription,
            },
        }),
    )
}

export type DeprecatedZipCodes = string[]

async function fetchDeprecatedZipCodes(pageSet: OrderSheetPageSetType): Promise<{
    orderDeprecateCodes: DeprecatedZipCodes
}> {
    const url = DeliveryApiUrlInfo[pageSet].deprecatedZipCodes
    const baseUrl = getBaseUrl(pageSet)
    const api = getApi(baseUrl)
    return createWrapper<{
        orderDeprecateCodes: DeprecatedZipCodes
    }>(api.get(url))
}

/**
 * 배송지/우편번호 개선 이후 사용될 통합배송지 api
 */
const AddressBookApiUrl = {
    /** 배송지 */
    delivery: '/delivery/address',
    /** 출입방법/수령위치 */
    entrance: '/delivery/entrance',
    /** 주소 검증 */
    validateAddressUrl: '/delivery/validateAddress',
}

const api = getApi(getCommonBaseUrl())

/** 주소 검증 */
const validateAddress = async ({
    zipCode,
    baseAddress,
    useRoadNameAddress,
    requestReason,
}: {
    zipCode: string
    baseAddress: string
    useRoadNameAddress: boolean
    requestReason: string
}) => {
    const url = AddressBookApiUrl.validateAddressUrl
    return createWrapper<ValidateAddressResponse>(
        api.post(url, {zipCode, baseAddress, useRoadNameAddress, requestReason}),
    )
}

/** 출입방법/수령위치 단건 조회 */
const getEntrance = async ({hash}: {hash: string}) => {
    const url = AddressBookApiUrl.entrance
    return createWrapper<GetEntranceResponse>(api.get(qs.stringifyUrl({url, query: {hash}})))
}

/** 출입방법/수령위치 삭제 */
const deleteEntrance = async ({hash}: {hash: string}) => {
    const url = AddressBookApiUrl.entrance
    return createWrapper<DeleteEntranceResponse>(api.delete(qs.stringifyUrl({url, query: {hash}})))
}

/** 출입방법/수령위치 추가 */
const addEntrance = async ({hash, entrance}: {hash: string; entrance: Entrance}) => {
    const url = AddressBookApiUrl.entrance
    return createWrapper<AddEntranceResponse>(api.post(qs.stringifyUrl({url, query: {hash}}), {entrance}))
}

/** 출입방법/수령위치 수정 */
const editEntrance = async ({hash, entrance}: {hash: string; entrance: Entrance}) => {
    const url = AddressBookApiUrl.entrance
    return createWrapper<EditEntranceResponse>(api.put(qs.stringifyUrl({url, query: {hash}}), {entrance}))
}

/** 배송지 변경 */
const changeDelivery = async ({
    orderNo,
    orderDeliveryAddress,
    uniqueMemo,
    memos,
    overseasDelivery,
    personalCustomsCode,
    addressBy,
}: ChangeDeliveryParams) => {
    const url = AddressBookApiUrl.delivery

    return createWrapper<ChangeDeliveryResponse>(
        api.put(url, {
            orderNo,
            orderDeliveryAddress,
            memos,
            uniqueMemo,
            overseasDelivery,
            personalCustomsCode,
            addressBy,
        }),
    )
}

export default {
    getDeliveryAddressFee,
    getArrivalGuaranteeValidate,
    fetchDeprecatedZipCodes,
    changeDelivery,
    validateAddress,
    getEntrance,
    deleteEntrance,
    addEntrance,
    editEntrance,
}
